/**
 * Shared formatters + style classes for trader/leaderboard tables.
 *
 * Used by /pits/{slug}/members. The /traders page (UsersGrid.tsx) has
 * private duplicates of these that should be migrated to use this
 * module — TODO once we're sure nothing regresses on the high-traffic
 * traders surface.
 */

/**
 * "$1.2M" / "$45.3K" / "$123" — never returns "—".
 * For empty cells use the caller's own fallback.
 */
export function formatVolume(volume: number | undefined | null): string {
  const n = Number(volume) || 0;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

/**
 * Like formatVolume but with 2 decimals — used for PnL where precision
 * matters more than density.
 */
export function formatCurrency(value: number | undefined | null): string {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(2)}`;
}

export function formatPct(n: number | undefined | null): string {
  const v = Number(n) || 0;
  return `${(v * 100).toFixed(1)}%`;
}

export function formatNum(n: number | undefined | null): string {
  const v = Number(n) || 0;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
}

export function formatSinceShort(iso: string): string {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return "today";
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
}

/** Trading style → chip classes. Colors mirror /traders UsersGrid. */
export const TRADING_STYLE_CHIP: Record<string, string> = {
  WHALE: "bg-blue-500/10 text-blue-400 border border-blue-500/20",
  MARKET_MAKER: "bg-purple-500/10 text-purple-400 border border-purple-500/20",
  ACTIVE_TRADER: "bg-orange-500/10 text-orange-400 border border-orange-500/20",
  DEGEN: "bg-red-500/10 text-red-400 border border-red-500/20",
  HIGH_CONVICTION: "bg-amber-500/10 text-amber-400 border border-amber-500/20",
  CONTRARIAN: "bg-violet-500/10 text-violet-400 border border-violet-500/20",
  VALUE_HUNTER: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20",
  BUY_AND_HOLD: "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20",
};

export const TRADING_STYLE_DEFAULT_CHIP =
  "bg-white/[0.06] text-gray-400 border border-white/[0.08]";

/** "MARKET_MAKER" → "Market Maker" */
export function formatTradingStyle(style: string): string {
  return style
    .replace(/_/g, " ")
    .split(" ")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

/** Color tokens used across leaderboard tables. Centralised to make
 *  visual drift between surfaces impossible. */
export const PNL_COLORS = {
  profit: "text-[#00FF9D]",
  loss: "text-[#FF4444]",
} as const;
